import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, CheckCircle, XCircle, Search, ChevronLeft, ChevronRight, BarChart3, Send, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const emailTypes = [
  { value: 'PASSWORD_RESET', label: 'Réinitialisation mot de passe' },
  { value: 'EMAIL_VERIFICATION', label: 'Vérification email' },
  { value: 'SUSPICIOUS_LOGIN', label: 'Connexion suspecte' },
  { value: 'ACCOUNT_LOCKED', label: 'Compte verrouillé' },
  { value: 'INTERPELLATION', label: 'Interpellation' },
  { value: 'PROLONGATION_REQUEST', label: 'Demande prolongation' },
  { value: 'DETENTION_OVERDUE', label: 'Détention excessive' },
];

const AdminEmailHistoryPage = () => {
  const { language } = useAuth();
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ email_type: '', status: '', search: '' });

  useEffect(() => { loadEmails(); }, [page, filters]);
  useEffect(() => { loadStats(); }, []);

  const loadEmails = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 30, ...filters };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await axios.get(`${API}/api/admin/email-history`, { headers: getAuthHeaders(), params });
      setEmails(res.data.emails || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/email-history/stats`, { headers: getAuthHeaders() });
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getTypeBadge = (type) => {
    const found = emailTypes.find(t => t.value === type);
    return found?.label || type || 'Autre';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="email-history-page">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <Mail className="w-8 h-8 text-primary" />
            {language === 'fr' ? 'Historique des Emails' : 'Email History'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'fr' ? 'Tous les emails envoyés par le système' : 'All emails sent by the system'}
          </p>
        </div>

        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total emails</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.sent}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Envoyés' : 'Sent'}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.failed}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Échoués' : 'Failed'}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'fr' ? 'Rechercher par email ou sujet...' : 'Search by email or subject...'}
                    className="pl-10"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && loadEmails()}
                    data-testid="email-search"
                  />
                </div>
              </div>
              <div className="w-[200px]">
                <Select value={filters.email_type || "ALL"} onValueChange={(v) => { setFilters({ ...filters, email_type: v === "ALL" ? "" : v }); setPage(1); }}>
                  <SelectTrigger data-testid="filter-email-type"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous les types' : 'All types'}</SelectItem>
                    {emailTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[150px]">
                <Select value={filters.status || "ALL"} onValueChange={(v) => { setFilters({ ...filters, status: v === "ALL" ? "" : v }); setPage(1); }}>
                  <SelectTrigger data-testid="filter-email-status"><SelectValue placeholder="Statut" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    <SelectItem value="SENT">{language === 'fr' ? 'Envoyé' : 'Sent'}</SelectItem>
                    <SelectItem value="FAILED">{language === 'fr' ? 'Échoué' : 'Failed'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={() => { setFilters({ email_type: '', status: '', search: '' }); setPage(1); }}>
                {language === 'fr' ? 'Réinitialiser' : 'Reset'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr className="bg-muted/50">
                    <th>{language === 'fr' ? 'Destinataire' : 'Recipient'}</th>
                    <th>{language === 'fr' ? 'Sujet' : 'Subject'}</th>
                    <th>Type</th>
                    <th>{language === 'fr' ? 'Statut' : 'Status'}</th>
                    <th>Date</th>
                    <th>{language === 'fr' ? 'Erreur' : 'Error'}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>{Array.from({ length: 6 }).map((_, j) => <td key={j}><div className="h-4 skeleton" /></td>)}</tr>
                    ))
                  ) : emails.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-muted-foreground">
                        {language === 'fr' ? 'Aucun email envoyé' : 'No emails sent'}
                      </td>
                    </tr>
                  ) : (
                    emails.map((email, i) => (
                      <tr key={email.email_id || i} data-testid={`email-row-${i}`}>
                        <td>
                          <span className="font-mono text-sm">{email.to_email}</span>
                        </td>
                        <td className="max-w-[250px] truncate text-sm">{email.subject}</td>
                        <td>
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                            {getTypeBadge(email.email_type)}
                          </span>
                        </td>
                        <td>
                          {email.status === 'SENT' ? (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 inline-flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> {language === 'fr' ? 'Envoyé' : 'Sent'}
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 inline-flex items-center gap-1">
                              <XCircle className="w-3 h-3" /> {language === 'fr' ? 'Échoué' : 'Failed'}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="text-sm text-muted-foreground">
                            {new Date(email.sent_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}
                          </span>
                        </td>
                        <td>
                          {email.error && (
                            <span className="text-xs text-red-500 flex items-center gap-1 max-w-[200px] truncate" title={email.error}>
                              <AlertTriangle className="w-3 h-3 shrink-0" /> {email.error}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {language === 'fr' ? `${total} email(s) au total` : `${total} total email(s)`}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminEmailHistoryPage;
